/*
 * wheel-finish-picker.js
 *
 * Per-panel finish picker for the wheel PDP.
 *
 * Each panel ([data-position="front"] / [data-position="rear"]) renders
 * its own picker [data-ew-finish-picker], so staggered orders can pick
 * a different finish for front vs. rear pairs.
 *
 * Layout:
 *   FinishModal — singleton owning the modal DOM, filter/search/grid
 *                 events, and the activePicker pointer.
 *   FinishPicker — one per panel; owns the trigger row + the per-panel
 *                  hidden inputs + the panel's committed selection.
 *
 * Per-panel hidden inputs (rendered by snippets/wheel-finish-picker.liquid):
 *   [data-ew-finish-input][data-key="Finish"]
 *   [data-ew-finish-input][data-key="_Finish Category"]
 *   [data-ew-finish-input][data-key="_Finish Handle"]
 *
 * These are read by buildPropsForPanel() in main-product-wheel.liquid's
 * inline JS when the form submits via window.ewCart.add(item) per panel.
 *
 * Default state on every page load = nothing selected. We do not restore
 * from sessionStorage — selection is intentional per visit.
 */
(function () {
  'use strict';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ============================================================
     FinishModal — singleton tied to [data-ew-finish-modal]
     ============================================================ */
  function FinishModal(el) {
    this.el = el;
    this.activePicker = null;
    this.staged = null; /* {handle, name, category, image} | null */
    this.activeCategory = 'all';
    this.search = '';
    this.invoker = null; /* element that opened the modal; focus restored to it on close */
  }

  FinishModal.prototype.bind = function () {
    var self = this;

    var closeBtn = $('[data-ew-finish-close]', this.el);
    if (closeBtn) closeBtn.addEventListener('click', function () { self.cancel(); });

    this.el.addEventListener('click', function (e) {
      if (e.target === self.el) self.cancel();
    });
    document.addEventListener('keydown', function (e) {
      if (self.el.getAttribute('data-open') !== 'true') return;
      if (e.key === 'Escape') self.cancel();
    });

    $$('[data-ew-finish-chip]', this.el).forEach(function (chip) {
      chip.addEventListener('click', function () {
        self.setCategory(chip.getAttribute('data-cat') || 'all');
      });
    });

    var searchInput = $('[data-ew-finish-search]', this.el);
    if (searchInput) {
      searchInput.addEventListener('input', function () {
        self.search = searchInput.value.trim().toLowerCase();
        self.applyFilter();
      });
    }

    $$('[data-ew-finish-card]', this.el).forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (e.target.closest('.ew-finish-card__info')) return;
        self.stage(card);
      });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          if (e.target.closest('.ew-finish-card__info')) return;
          e.preventDefault();
          self.stage(card);
        }
      });
    });

    var confirmBtn = $('[data-ew-finish-confirm]', this.el);
    var cancelBtn = $('[data-ew-finish-cancel]', this.el);
    if (confirmBtn) confirmBtn.addEventListener('click', function () { self.confirm(); });
    if (cancelBtn) cancelBtn.addEventListener('click', function () { self.cancel(); });
  };

  FinishModal.prototype.cardData = function (card) {
    return {
      handle: card.getAttribute('data-handle') || '',
      name: card.getAttribute('data-name') || '',
      category: card.getAttribute('data-category') || '',
      image: card.getAttribute('data-image') || '',
    };
  };

  FinishModal.prototype.open = function (picker) {
    var self = this;
    this.activePicker = picker;
    /* a11y: capture the invoker BEFORE moving focus so removeTrapFocus can
       restore focus to it on close (WCAG 2.4.3). */
    this.invoker = (document.activeElement && typeof document.activeElement.focus === 'function')
      ? document.activeElement
      : null;
    /* Stage starts at the picker's committed selection so reopening to "edit" works. */
    this.staged = picker.committed;

    $$('[data-ew-finish-card]', this.el).forEach(function (c) {
      var pre = self.staged && c.getAttribute('data-handle') === self.staged.handle;
      c.setAttribute('aria-selected', pre ? 'true' : 'false');
    });

    /* Header subtitle: show which panel the user is choosing for, in staggered mode. */
    var pos = picker.position;
    var title = $('[data-ew-finish-modal-title]', this.el);
    var titleEl = $('.ew-finish-modal__title', this.el);
    if (titleEl) {
      titleEl.textContent = (pos === 'rear') ? 'Choose a rear finish' : 'Choose a finish';
    }

    this.updateConfirmButton();
    this.el.setAttribute('data-open', 'true');
    document.body.setAttribute('data-ew-finish-modal-open', 'true');

    /* Reset filter + search to defaults on each open */
    this.setCategory('all');
    var searchInput = $('[data-ew-finish-search]', this.el);
    if (searchInput) {
      searchInput.value = '';
      this.search = '';
      this.applyFilter();
    }

    /* a11y: contain Tab focus inside the dialog and give the search input
       initial focus. trapFocus (global.js) owns the initial .focus() — do
       NOT also call searchInput.focus() here (no double-focus). The modal
       is already displayed (data-open="true" set above) so focus can land. */
    if (typeof trapFocus === 'function') {
      trapFocus(this.el, searchInput || this.el);
    } else if (searchInput) {
      searchInput.focus();
    }
  };

  FinishModal.prototype.cancel = function () {
    this.el.setAttribute('data-open', 'false');
    document.body.removeAttribute('data-ew-finish-modal-open');
    this.staged = null;
    this.activePicker = null;
    /* a11y: release the focus trap and restore focus to the invoker (the
       trigger that opened the modal). Single teardown path — confirm()
       also calls cancel(), so this covers close button / scrim / Escape /
       Cancel / Confirm uniformly (WCAG 2.4.3, 2.1.2). */
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.invoker || null);
    } else if (this.invoker && typeof this.invoker.focus === 'function') {
      this.invoker.focus();
    }
    this.invoker = null;
  };

  FinishModal.prototype.stage = function (card) {
    $$('[data-ew-finish-card]', this.el).forEach(function (c) { c.setAttribute('aria-selected', 'false'); });
    card.setAttribute('aria-selected', 'true');
    this.staged = this.cardData(card);
    this.updateConfirmButton();
  };

  FinishModal.prototype.confirm = function () {
    if (!this.staged || !this.activePicker) return;
    this.activePicker.commit(this.staged);
    this.cancel();
  };

  FinishModal.prototype.updateConfirmButton = function () {
    var btn = $('[data-ew-finish-confirm]', this.el);
    if (!btn) return;
    btn.disabled = !this.staged;
  };

  FinishModal.prototype.setCategory = function (cat) {
    this.activeCategory = cat;
    $$('[data-ew-finish-chip]', this.el).forEach(function (chip) {
      chip.setAttribute('aria-pressed', chip.getAttribute('data-cat') === cat ? 'true' : 'false');
    });
    this.applyFilter();
  };

  FinishModal.prototype.applyFilter = function () {
    var cat = this.activeCategory;
    var q = this.search;
    var visible = 0;
    $$('[data-ew-finish-card]', this.el).forEach(function (card) {
      var matchCat = (cat === 'all') || (',' + cat + ',').indexOf(',' + card.getAttribute('data-category') + ',') !== -1;
      var matchSearch = !q || (card.getAttribute('data-name') || '').toLowerCase().indexOf(q) !== -1;
      var show = matchCat && matchSearch;
      card.setAttribute('data-hidden', show ? 'false' : 'true');
      if (show) visible++;
    });
    var counter = $('[data-ew-finish-count]', this.el);
    if (counter) counter.textContent = visible + ' finish' + (visible === 1 ? '' : 'es');
  };

  /* ============================================================
     FinishPicker — one per panel
     ============================================================ */
  function FinishPicker(root, modal) {
    this.root = root;
    this.modal = modal;
    this.position = root.getAttribute('data-finish-position') || 'front';
    this.committed = null;
  }

  FinishPicker.prototype.bind = function () {
    var self = this;
    var openBtn = $('[data-ew-finish-open]', this.root);
    var changeBtn = $('[data-ew-finish-change]', this.root);
    if (openBtn) openBtn.addEventListener('click', function () { self.modal.open(self); });
    if (changeBtn) changeBtn.addEventListener('click', function () { self.modal.open(self); });
  };

  FinishPicker.prototype.commit = function (data) {
    this.committed = data;
    this.renderSelectedRow(data);
    this.writeHiddenInputs(data);
    /* Bubble to section root so inline JS re-gates ATC. */
    this.root.dispatchEvent(new CustomEvent('ew:finish-selected', {
      bubbles: true,
      detail: { position: this.position, data: data },
    }));
  };

  FinishPicker.prototype.clear = function () {
    this.committed = null;
    this.renderSelectedRow(null);
    this.writeHiddenInputs(null);
    this.root.dispatchEvent(new CustomEvent('ew:finish-cleared', {
      bubbles: true,
      detail: { position: this.position },
    }));
  };

  FinishPicker.prototype.renderSelectedRow = function (data) {
    var openBtn = $('[data-ew-finish-open]', this.root);
    var sel = $('[data-ew-finish-selected]', this.root);
    if (!sel || !openBtn) return;
    if (!data) {
      openBtn.hidden = false;
      sel.hidden = true;
      return;
    }
    var img = $('.ew-finish-swatch', sel);
    var name = $('.ew-finish-name', sel);
    var cat = $('.ew-finish-cat', sel);
    if (img) { img.src = data.image; img.alt = data.name + ' finish swatch'; }
    if (name) name.textContent = data.name;
    if (cat) cat.textContent = (data.category || '').toUpperCase();
    openBtn.hidden = true;
    sel.hidden = false;
  };

  FinishPicker.prototype.writeHiddenInputs = function (data) {
    $$('[data-ew-finish-input]', this.root).forEach(function (input) {
      var key = input.getAttribute('data-key');
      var value = '';
      if (data) {
        if (key === 'Finish') value = data.name;
        else if (key === '_Finish Category') value = data.category;
        else if (key === '_Finish Handle') value = data.handle;
      }
      input.value = value;
    });
  };

  /* ============================================================
     Boot
     ============================================================ */
  function boot() {
    var modalEl = $('[data-ew-finish-modal]');
    if (!modalEl) return;
    var modal = new FinishModal(modalEl);
    modal.bind();
    $$('[data-ew-finish-picker]').forEach(function (root) {
      var p = new FinishPicker(root, modal);
      p.bind();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.EWFinishModal = FinishModal;
  window.EWFinishPicker = FinishPicker;
})();
